import { useEffect, useRef, useState } from 'react';
import { Outlet } from 'react-router';
import { Button, useToast } from '@efira/ui';

import { CenterColumn } from './CenterColumn';
import { LeftColumn } from './LeftColumn';
import { Resizer } from './Resizer';
import { RightColumn } from './RightColumn';
import { TOPBAR_HEIGHT } from './geometry';
import { Tour, tourSeen } from './Tour';
import { TopBarCenter, TopBarLeft, TopBarRight } from './TopBar';
import { Chat } from './right/Chat';
import { Gifs } from './right/Gifs';
import { Music } from './right/Music';
import { Pads } from './right/Pads';
import { useMediaTab } from './media';
import { Gifters } from './right/Gifters';
import { StatStrip } from './right/StatStrip';
import { ConsoleSessionProvider, useConsoleSession } from './session';
import { ConsoleStreamProvider } from './stream';
import { MediaTabProvider } from './media';
import { LayoutModeProvider } from './center/layoutMode';
import { useConsoleGeometry } from './useConsoleGeometry';
import { useConsoleHotkeys } from './hotkeys';
import { useToastLane } from './toastLane';
import { API_URL } from '../config';
import { deviceId, forgetReferral, takeReferral } from '../lib/device';

/**
 * Оболочка консоли: три колонки, верхняя полоса, модальный слой.
 *
 * ЧТО ЗАМЕНЯЕТ. Семь вкладок dashboard. Модель была неверной: стример в эфире
 * не переключает вкладки, он смотрит на один экран, где видно всё сразу
 * (docs/DECISIONS.md, запись [2026-09-18] об оболочке консоли).
 *
 * СТРАНИЦА ЦЕЛИКОМ НЕ ПРОКРУЧИВАЕТСЯ. Оболочка занимает ровно высоту окна, а
 * прокручиваются по отдельности левая колонка, центр и каждая лента правой.
 * Это требование, а не деталь раскладки: прокрутка всей страницы в консоли
 * эфира означает, что стример теряет из виду кадр.
 *
 * СЕТКА, А НЕ ТРИ ПАНЕЛИ. Верхняя полоса разрезана по границам колонок, и
 * каждая её треть едет вместе со своей границей. Это возможно только если
 * полоса - верхний ряд ТОЙ ЖЕ сетки; отдельная полоса над колонками так не
 * умеет.
 *
 * НИЖЕ 1024 - ПЕРЕКОМПОНОВКА, а не сжатый десктоп: три колонки складываются в
 * одну с переключателем. Три колонки по 130 пикселей - не консоль.
 */
/**
 * ЗАКРЕПЛЕНИЕ ПРИВЕДЁННОГО АВТОРА - ПРИ ПЕРВОМ ВХОДЕ.
 *
 * Регистрация сессии не даёт: сначала подтверждение почты. Поэтому код
 * партнёрской ссылки лежит в браузере с момента перехода и тратится здесь -
 * в первом же входе в консоль.
 *
 * Ответ сервера любой - код забывается: и «закрепили», и «своя же ссылка»
 * окончательны. Повторная попытка ничего не изменит, а код, живущий в
 * браузере вечно, однажды закрепит не того человека.
 */
function useReferralBind(): void {
  const toast = useToast();

  useEffect(() => {
    const code = takeReferral();

    if (code === null || code === '') return;

    void fetch(`${API_URL}/api/affiliate/bind`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ code, device: deviceId() }),
    })
      .then(async (response) => {
        forgetReferral();

        const result = (await response.json()) as { ok?: boolean; message?: string };

        /*
         * НИ ОДНА МУТАЦИЯ НЕ ЗАВЕРШАЕТСЯ ТИХО - правило проекта, и в контуре
         * чужих денег оно тем более. Незасчитанный привод человек обязан
         * увидеть: иначе партнёр смотрит на «Регистрации: 0» и не знает,
         * почему, а причина у сервера написана словами.
         */
        if (result.ok === true) {
          toast.show({
            tone: 'success',
            title: 'Вы пришли по партнёрской ссылке',
            text: 'Автор, который вас позвал, получит отчисления с ваших платежей.',
          });

          return;
        }

        toast.show({
          tone: 'warning',
          title: 'Партнёрская ссылка не засчитана',
          ...(result.message === undefined ? {} : { text: result.message }),
        });
      })
      .catch(() => null);
  }, [toast]);
}

export function ConsoleShell() {
  useReferralBind();
  /* Горячие клавиши живут на оболочке, а не на кнопках: кнопка пада может
     быть за пределами видимой области, а клавиша обязана работать всегда,
     пока фокус в консоли (решение владельца 2026-09-26, п. 6). */
  useConsoleHotkeys(true);

  const { geometry, width, stacked, setLeft, setRight, setPads } = useConsoleGeometry();
  /* Полоса тостов - там, где нет органов управления (единица 21, N-02). */
  useToastLane(stacked);
  /**
   * Страница под консолью не прокручивается НИКОГДА.
   *
   * Консоль занимает ровно экран, а прокручиваются зоны внутри неё. Одного
   * `h-dvh` с `overflow-hidden` на оболочке для этого мало: когда содержимое
   * зоны выше экрана, `scrollHeight` документа всё равно вырастает, и на
   * узком окне появляется вторая полоса прокрутки поверх первой. Поймано
   * сценарием «страница не прокручивается» на ширине 820, когда каталог
   * перестал помещаться в экран.
   *
   * Правится на документе и снимается при уходе с консоли: прежние экраны
   * зон под `/app/legacy/*` прокручиваются штатно.
   */
  useEffect(() => {
    const root = document.documentElement;
    const previous = root.style.overflow;
    root.style.overflow = 'hidden';

    return () => {
      root.style.overflow = previous;
    };
  }, []);

  /**
   * Гид. Запускается кнопкой в подвале и один раз сам - при первом входе.
   *
   * Сам он показывается ПОСЛЕ появления консоли: тур подсвечивает её зоны, и
   * без них подсвечивать нечего. Признак «уже видел» живёт в браузере: тур
   * объясняет интерфейс, а не хранит состояние аккаунта.
   */
  const [tourOpen, setTourOpen] = useState(false);
  const [tourChecked, setTourChecked] = useState(false);

  /* ЖДАТЬ БОЛЬШЕ НЕЧЕГО. До единицы 15 консоль ждала загрузки скрипта
     поведения прежней библиотеки и до неё показывала «Загрузка…»: без
     скрипта разметка стояла без поведения. Поведение теперь в React, и
     ждать его не нужно - экран ожидания снят целиком. */
  useEffect(() => {
    if (tourChecked) return;
    const id = window.setTimeout(() => {
      setTourChecked(true);
      if (!tourSeen()) setTourOpen(true);
    }, 0);
    return () => {
      window.clearTimeout(id);
    };
  }, [tourChecked]);
  const bodyRef = useRef<HTMLDivElement>(null);
  const [bodyBox, setBodyBox] = useState({ top: TOPBAR_HEIGHT, height: 0 });

  /**
   * Опора для горизонтальной границы внутри правой колонки: доля берётся от
   * высоты тела, и высоту надо знать в пикселях, а не в процентах.
   *
   * Замер идёт через ResizeObserver, а не вызовом в теле эффекта: тело эффекта,
   * меняющее состояние, вызывает каскад перерисовок, и правило проекта это
   * запрещает. Наблюдатель сообщает размер СОБЫТИЕМ - и первый раз тоже.
   */
  useEffect(() => {
    const node = bodyRef.current;
    if (node === null) return;

    const observer = new ResizeObserver(() => {
      const rect = node.getBoundingClientRect();
      setBodyBox({ top: rect.top, height: rect.height });
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [stacked]);

  return (
    <ConsoleSessionProvider>
      <ConsoleStreamProvider>
        <MediaTabProvider>
          <LayoutModeProvider>
            {stacked ? (
              <StackedConsole
                onGuide={() => {
                  setTourOpen(true);
                }}
              />
            ) : null}
            {!stacked && (
              <div
                data-console-shell
                className="bg-background text-foreground relative grid h-dvh w-screen overflow-hidden"
                style={{
                  gridTemplateColumns: `${geometry.left}fr ${1 - geometry.left - geometry.right}fr ${geometry.right}fr`,
                  gridTemplateRows: `${TOPBAR_HEIGHT}px minmax(0, 1fr)`,
                }}
              >
                {/* Верхний ряд: три трети полосы, каждая в своей колонке. */}
                <div className="border-foreground/10 min-w-0 border-r border-b">
                  <TopBarLeft />
                </div>
                <div className="border-foreground/10 min-w-0 border-b">
                  <TopBarCenter />
                </div>
                <div className="border-foreground/10 min-w-0 border-b border-l">
                  <TopBarRight />
                </div>

                {/* Нижний ряд: тело трёх колонок. */}
                <div className="border-foreground/10 min-h-0 min-w-0 border-r">
                  <LeftColumn
                    onGuide={() => {
                      setTourOpen(true);
                    }}
                  />
                </div>
                <div ref={bodyRef} className="bg-muted/40 min-h-0 min-w-0">
                  <CenterColumn />
                </div>
                <div className="border-foreground/10 min-h-0 min-w-0 border-l">
                  <RightColumn
                    pads={geometry.pads}
                    onPadsChange={setPads}
                    height={bodyBox.height}
                    top={bodyBox.top}
                  />
                </div>

                {/* Две вертикальные границы идут сквозь полосу и тело - как у эталона. */}
                <Resizer
                  orientation="vertical"
                  value={geometry.left}
                  onChange={setLeft}
                  label="Ширина левой колонки"
                  extent={width}
                  style={{ left: `${geometry.left * 100}%` }}
                />
                <Resizer
                  orientation="vertical"
                  value={geometry.right}
                  onChange={setRight}
                  label="Ширина правой колонки"
                  extent={width}
                  fromEnd
                  style={{ left: `${(1 - geometry.right) * 100}%` }}
                />
              </div>
            )}

            {tourOpen && (
              <Tour
                onFinish={() => {
                  setTourOpen(false);
                }}
              />
            )}

            {/* Модальный слой: маршруты открываются ПОВЕРХ живой консоли. */}
            <Outlet />
          </LayoutModeProvider>
        </MediaTabProvider>
      </ConsoleStreamProvider>
    </ConsoleSessionProvider>
  );
}

/**
 * Узкое окно: одна колонка с переключателем зон.
 *
 * Три колонки эталона на 390 пикселях дали бы по 106 пикселей на каждую - это
 * не уменьшенная консоль, это нечитаемая. Поэтому зоны показываются по одной,
 * и каждая по-прежнему прокручивается сама, а страница - нет.
 */
function StackedConsole({ onGuide }: { onGuide: () => void }) {
  const [zone, setZone] = useState<'left' | 'center' | 'right'>('center');

  return (
    <div
      data-console-shell="stacked"
      /*
       * `fixed`, а не `h-dvh`: в одноколоночной раскладке содержимое зоны
       * выше экрана - каталог из десяти игр в узкой колонке, - и при `h-dvh`
       * документ всё равно считает его своей высотой. Появляется вторая
       * полоса прокрутки поверх первой. Вынос из потока это снимает.
       * Поймано сценарием «страница не прокручивается» на ширине 820.
       */
      className="bg-background text-foreground fixed inset-0 flex flex-col overflow-hidden"
    >
      <div className="border-foreground/10 shrink-0 border-b" style={{ height: TOPBAR_HEIGHT }}>
        {zone === 'left' && <TopBarLeft />}
        {zone === 'center' && <TopBarCenter />}
        {zone === 'right' && <TopBarRight />}
      </div>

      <div className="min-h-0 flex-1">
        {zone === 'left' && (
          <LeftColumn
            onGuide={() => {
              onGuide();
            }}
          />
        )}
        {zone === 'center' && <CenterColumn />}
        {zone === 'right' && <StackedRight />}
      </div>

      <nav className="border-foreground/10 flex shrink-0 border-t" aria-label="Зоны консоли">
        {(
          [
            ['left', 'Управление'],
            ['center', 'Оверлей'],
            ['right', 'Эфир'],
          ] as const
        ).map(([id, title]) => (
          <Button
            variant="ghost"
            key={id}
            aria-current={zone === id ? 'true' : undefined}
            onClick={() => {
              setZone(id);
            }}
            /**
             * Активная зона отмечена ПОЛОСОЙ, а не цветом текста: primary на
             * светлом фоне даёт 2,4:1 и не проходит AA, а полоса читается и
             * при дальтонизме, потому что дублируется начертанием.
             */
            className={[
              'flex-1 border-t-2 py-2 text-sm',
              zone === id
                ? 'border-primary text-foreground font-semibold'
                : 'text-foreground/80 border-transparent',
            ].join(' ')}
          >
            {title}
          </Button>
        ))}
      </nav>
    </div>
  );
}

/** Раздел медиа в сложенной раскладке: тот же выбор, что в полосе. */
function StackedMedia() {
  const { tab } = useMediaTab();

  return tab === 'sounds' ? <Pads /> : tab === 'gif' ? <Gifs /> : <Music />;
}

/**
 * Узкое окно, зона «Эфир»: та же лента, те же счётчики, те же гифтеры.
 *
 * НЕ УРЕЗАННАЯ КОПИЯ, А ТЕ ЖЕ СЕКЦИИ. Заглушка вместо ленты на узком окне
 * означала бы, что с телефона эфир не видно вовсе, - а именно с телефона
 * стример и смотрит в чат, когда сам стоит перед камерой.
 *
 * Область падов не тянется: тянуть нечего, зона одна.
 */
function StackedRight() {
  const session = useConsoleSession();

  return (
    <div data-right="column" className="flex h-full min-h-0 flex-col">
      {/* Медиа и на узком окне: с телефона стример нажимает пады, ищет
          гифки и ставит музыку так же, как с ноутбука. */}
      <div className="max-h-56 shrink-0">
        <StackedMedia />
      </div>

      {session.game !== null && <StatStrip />}
      <Chat />
      <Gifters />
    </div>
  );
}
